import { useState, useRef, useEffect } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CalendarIcon } from "lucide-react";

export default function DatePicker({ value, onChange, label = "Date", required = false, placeholder = "DD/MM/YYYY" }) {
  const [open, setOpen] = useState(false);
  // Format value to DD/MM/YYYY string regardless of input type
  const formatValue = (val) => {
    if (!val) return "";
    if (val instanceof Date && isValid(val)) return format(val, "dd/MM/yyyy");
    if (typeof val === "string" && val.length === 10 && val.includes("/")) return val;
    // Try parsing ISO or other date strings
    if (typeof val === "string") {
      const d = new Date(val);
      if (isValid(d)) return format(d, "dd/MM/yyyy");
    }
    return "";
  };

  const [inputValue, setInputValue] = useState(() => formatValue(value));
  const triggerRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    const formatted = formatValue(value);
    if (formatted && formatted !== inputValue) {
      setInputValue(formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleCalendarSelect = (date) => {
    if (date) {
      const formatted = format(date, "dd/MM/yyyy");
      setInputValue(formatted);
      onChange(formatted);
    }
    // Close popover with a small delay for Safari
    setTimeout(() => setOpen(false), 50);
  };

  const handleInputChange = (e) => {
    let val = e.target.value;
    // Auto-format: add slashes as user types
    const digits = val.replace(/[^\d]/g, "");
    if (digits.length <= 2) {
      val = digits;
    } else if (digits.length <= 4) {
      val = digits.slice(0, 2) + "/" + digits.slice(2);
    } else {
      val = digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4, 8);
    }
    setInputValue(val);
    
    // Only call onChange if we have a complete valid date
    if (val.length === 10) {
      const parsed = parse(val, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) {
        onChange(val);
      }
    }
  };

  const getCalendarDate = () => {
    if (inputValue && inputValue.length === 10) {
      const parsed = parse(inputValue, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) return parsed;
    }
    return undefined;
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label className="flex items-center gap-1">
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <div className="flex gap-2">
        <Input
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          maxLength={10}
          className="flex-1"
          data-testid="date-input"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button ref={triggerRef} variant="outline" size="icon" type="button" data-testid="date-picker-btn">
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="end"
            side="bottom"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <Calendar
              mode="single"
              selected={getCalendarDate()}
              onSelect={handleCalendarSelect}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
